import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { AuscultationPlayer, LUNG_SOUNDS, BOWEL_SOUNDS, HEART_SOUNDS } from '../components/AuscultationAudio';
import { PatientVitalsGrid, PatientResponseNote } from '../components/PatientVitalsGrid';
import { RapidResponseAlert } from '../components/RapidResponseAlert';
import { VitalsTrendChart } from '../components/VitalsTrendChart';
import { FlagConcernsPanel } from '../components/FlagConcernsPanel';
import { OrderReviewList, ReviewResults } from '../components/OrderReview';
import { OrderScorecard } from '../components/OrderScorecard';

const historyTitle = (fb) => {
  if (fb.feedback_items) return `Priority order: ${fb.feedback_items.map(i => i.patient_name).join(' > ')}`;
  const action = fb.action || (fb.quality ? `Assessment (${fb.quality})` : fb.review_results ? 'Chart Review' : fb.concern_results ? 'Flag Concerns' : 'Action');
  return `${fb.patient_name || 'Patient'} - ${String(action).replace(/_/g, ' ')}`;
};
import { 
  Stethoscope, Activity, AlertTriangle, CheckCircle, XCircle, Clock,
  User, Heart, Brain, ArrowLeft, ArrowRight, RefreshCw, ChevronRight, Shield,
  AlertCircle, Award, Target, FileText, Pill, Play, Eye, ClipboardList,
  Users, Wind, Droplets, ThermometerSun, Hand, Flag, Siren, Thermometer,
  Gauge, HeartPulse, Droplet, BedDouble, MessageSquareWarning, Building2,
  Ambulance, Baby, Volume2, BookOpen, X, Info
} from 'lucide-react';

// Medication Lookup Database - NCLEX-focused drug information
const MEDICATION_DATABASE = {
  // Cardiac Medications
  "aspirin": {
    genericName: "Aspirin (Acetylsalicylic Acid)",
    drugClass: "Antiplatelet / NSAID",
    mechanism: "Irreversibly inhibits cyclooxygenase (COX), preventing thromboxane A2 synthesis and platelet aggregation",
    indications: ["Acute Coronary Syndrome", "MI prophylaxis", "Stroke prevention", "Pain/fever"],
    sideEffects: ["GI bleeding", "Tinnitus (toxicity sign)", "Bruising", "Allergic reactions"],
    nursingConsiderations: [
      "Assess for bleeding (gums, bruising, black stools)",
      "Hold before surgery as directed",
      "Give with food to reduce GI upset",
      "Monitor for tinnitus (sign of toxicity)",
      "Check for aspirin allergy before administration"
    ],
    nclexTip: "Aspirin is the FIRST drug given in suspected MI - 'chew and swallow' for faster absorption"
  },
  "heparin": {
    genericName: "Heparin (Unfractionated)",
    drugClass: "Anticoagulant",
    mechanism: "Enhances antithrombin III activity, inhibiting thrombin and factor Xa",
    indications: ["DVT/PE treatment", "ACS", "Atrial fibrillation", "Dialysis"],
    sideEffects: ["Bleeding", "Heparin-induced thrombocytopenia (HIT)", "Osteoporosis (long-term)"],
    nursingConsiderations: [
      "Monitor aPTT (therapeutic: 1.5-2.5x normal)",
      "Assess for bleeding signs",
      "NEVER give IM - use SubQ or IV only",
      "Antidote: Protamine sulfate",
      "Monitor platelet count for HIT"
    ],
    nclexTip: "Heparin uses aPTT monitoring; Warfarin uses PT/INR"
  },
  "morphine": {
    genericName: "Morphine Sulfate",
    drugClass: "Opioid Analgesic",
    mechanism: "Binds to mu-opioid receptors in CNS, altering pain perception and emotional response",
    indications: ["Severe pain", "MI pain", "Pulmonary edema", "Dyspnea in palliative care"],
    sideEffects: ["Respiratory depression", "Hypotension", "Constipation", "Nausea", "Sedation", "Urinary retention"],
    nursingConsiderations: [
      "Assess respiratory rate BEFORE giving (hold if <12)",
      "Monitor BP - causes vasodilation",
      "Have Narcan (naloxone) available",
      "Assess pain level using appropriate scale",
      "Monitor sedation level",
      "Prevent constipation with stool softeners"
    ],
    nclexTip: "MONA for MI: Morphine, Oxygen, Nitroglycerin, Aspirin - but morphine use is now controversial"
  },
  "nitroglycerin": {
    genericName: "Nitroglycerin (NTG)",
    drugClass: "Nitrate / Vasodilator",
    mechanism: "Relaxes vascular smooth muscle via nitric oxide, causing vasodilation (primarily venous)",
    indications: ["Angina", "Acute MI", "Hypertensive emergency", "Heart failure"],
    sideEffects: ["Hypotension", "Headache", "Flushing", "Reflex tachycardia", "Dizziness"],
    nursingConsiderations: [
      "Check BP before giving - HOLD if SBP <90",
      "Patient should sit or lie down",
      "SL: 1 tab q5min x3 max, then call 911",
      "Headache is expected - indicates effectiveness",
      "Remove old patches before applying new",
      "Store SL tabs in dark glass container"
    ],
    nclexTip: "SL nitroglycerin should relieve angina in 5 minutes. If 3 doses don't help = call 911"
  },
  "metoprolol": {
    genericName: "Metoprolol (Lopressor/Toprol-XL)",
    drugClass: "Beta-1 Selective Blocker",
    mechanism: "Blocks beta-1 adrenergic receptors in heart, reducing heart rate, contractility, and BP",
    indications: ["Hypertension", "Angina", "Heart failure", "Post-MI", "Arrhythmias"],
    sideEffects: ["Bradycardia", "Hypotension", "Fatigue", "Depression", "Masking hypoglycemia symptoms"],
    nursingConsiderations: [
      "Check HR and BP before giving",
      "HOLD if HR <60 or SBP <90",
      "Never stop abruptly - taper to prevent rebound",
      "Teach diabetics to monitor glucose (masks hypoglycemia)",
      "May worsen asthma/COPD"
    ],
    nclexTip: "Beta-blockers end in '-olol'. They treat HTN by slowing the heart (think 'slow-lol')"
  },
  "atorvastatin": {
    genericName: "Atorvastatin (Lipitor)",
    drugClass: "HMG-CoA Reductase Inhibitor (Statin)",
    mechanism: "Inhibits cholesterol synthesis enzyme, reducing LDL and increasing HDL",
    indications: ["Hyperlipidemia", "Atherosclerosis prevention", "Post-ACS"],
    sideEffects: ["Myopathy/rhabdomyolysis", "Liver toxicity", "GI upset", "Headache"],
    nursingConsiderations: [
      "Give at bedtime (cholesterol synthesis peaks at night)",
      "Monitor LFTs and report muscle pain",
      "Avoid grapefruit juice",
      "Report unexplained muscle weakness immediately",
      "Check CK if muscle symptoms occur"
    ],
    nclexTip: "Statins end in '-statin'. Rhabdomyolysis is the serious complication - watch for dark urine and muscle pain"
  },
  "enoxaparin": {
    genericName: "Enoxaparin (Lovenox)",
    drugClass: "Low Molecular Weight Heparin (LMWH)",
    mechanism: "Inhibits factor Xa and thrombin, preventing clot formation",
    indications: ["DVT prophylaxis", "DVT/PE treatment", "ACS", "Post-surgery prophylaxis"],
    sideEffects: ["Bleeding", "Thrombocytopenia", "Injection site bruising"],
    nursingConsiderations: [
      "Give SubQ only - NEVER IM or IV",
      "Do NOT expel air bubble from syringe",
      "Rotate injection sites (abdomen preferred)",
      "Do not rub injection site",
      "Antidote: Protamine (partial reversal only)"
    ],
    nclexTip: "Lovenox is given SubQ and does NOT require aPTT monitoring (unlike heparin)"
  },
  "oxycodone": {
    genericName: "Oxycodone (OxyContin, Percocet)",
    drugClass: "Opioid Analgesic",
    mechanism: "Binds to opioid receptors in CNS, altering pain perception",
    indications: ["Moderate to severe pain"],
    sideEffects: ["Respiratory depression", "Constipation", "Sedation", "Nausea", "Dependence"],
    nursingConsiderations: [
      "Assess respiratory rate before administering",
      "Monitor sedation level (RASS scale)",
      "Implement fall precautions",
      "Administer stool softeners prophylactically",
      "Assess pain before and after administration"
    ],
    nclexTip: "Always assess respiratory status BEFORE giving opioids. Hold if RR <12"
  },
  "insulin": {
    genericName: "Insulin (various types)",
    drugClass: "Antidiabetic - Hormone",
    mechanism: "Facilitates glucose uptake into cells, promotes glycogen storage",
    indications: ["Type 1 DM", "Type 2 DM", "DKA", "Hyperglycemia"],
    sideEffects: ["Hypoglycemia", "Lipodystrophy", "Weight gain", "Hypokalemia"],
    nursingConsiderations: [
      "ALWAYS verify with another nurse (high-alert med)",
      "Check blood glucose before administration",
      "Know onset, peak, and duration of each type",
      "Rotate injection sites",
      "Teach hypoglycemia signs: shakiness, sweating, confusion"
    ],
    nclexTip: "Rapid-acting insulins (lispro, aspart) = give with meals. Long-acting (glargine) = NO peak, given once daily"
  },
  "metformin": {
    genericName: "Metformin (Glucophage)",
    drugClass: "Biguanide - Oral Antidiabetic",
    mechanism: "Decreases hepatic glucose production and increases insulin sensitivity",
    indications: ["Type 2 DM", "PCOS", "Prediabetes"],
    sideEffects: ["GI upset (diarrhea, nausea)", "Lactic acidosis (rare)", "B12 deficiency"],
    nursingConsiderations: [
      "Give with meals to reduce GI upset",
      "Hold before contrast procedures",
      "Monitor renal function",
      "Teach signs of lactic acidosis",
      "Does NOT cause hypoglycemia when used alone"
    ],
    nclexTip: "Stop metformin 48 hours before and after IV contrast - risk of lactic acidosis"
  },
  "lisinopril": {
    genericName: "Lisinopril (Prinivil, Zestril)",
    drugClass: "ACE Inhibitor",
    mechanism: "Inhibits angiotensin-converting enzyme, reducing angiotensin II and aldosterone",
    indications: ["Hypertension", "Heart failure", "Diabetic nephropathy", "Post-MI"],
    sideEffects: ["Dry cough", "Hyperkalemia", "Angioedema", "Hypotension", "Dizziness"],
    nursingConsiderations: [
      "Monitor potassium levels",
      "Assess for dry cough (common reason for discontinuation)",
      "STOP immediately if angioedema occurs",
      "Avoid potassium supplements/salt substitutes",
      "Contraindicated in pregnancy"
    ],
    nclexTip: "ACE inhibitors end in '-pril'. Dry cough is common; switch to ARB (-sartan) if intolerable"
  },
  "vancomycin": {
    genericName: "Vancomycin (Vancocin)",
    drugClass: "Glycopeptide Antibiotic",
    mechanism: "Inhibits bacterial cell wall synthesis by binding to D-Ala-D-Ala",
    indications: ["MRSA infections", "C. diff colitis (oral)", "Serious gram-positive infections"],
    sideEffects: ["Nephrotoxicity", "Ototoxicity", "Red man syndrome", "Thrombophlebitis"],
    nursingConsiderations: [
      "Infuse over at least 60 minutes (prevents red man syndrome)",
      "Monitor trough levels (goal: 10-20 mcg/mL)",
      "Assess renal function and hearing",
      "Premedicate with antihistamine if red man syndrome occurs",
      "Monitor for superinfection"
    ],
    nclexTip: "Red man syndrome = infuse too fast. Slow the rate and give antihistamine"
  },
  "furosemide": {
    genericName: "Furosemide (Lasix)",
    drugClass: "Loop Diuretic",
    mechanism: "Inhibits sodium/potassium/chloride reabsorption in loop of Henle",
    indications: ["Heart failure", "Edema", "Hypertension", "Acute pulmonary edema"],
    sideEffects: ["Hypokalemia", "Dehydration", "Ototoxicity", "Hypotension", "Hyponatremia"],
    nursingConsiderations: [
      "Monitor potassium - may need supplements",
      "Weigh daily (same time, same scale)",
      "Monitor I&O",
      "Give in morning to prevent nocturia",
      "Assess for dehydration signs"
    ],
    nclexTip: "Loop diuretics 'waste' potassium. Monitor K+ and watch for digoxin toxicity if on both"
  },
  "norepinephrine": {
    genericName: "Norepinephrine (Levophed)",
    drugClass: "Vasopressor / Alpha-1 Agonist",
    mechanism: "Stimulates alpha-1 receptors causing vasoconstriction, increases BP",
    indications: ["Septic shock", "Cardiogenic shock", "Severe hypotension"],
    sideEffects: ["Tissue necrosis (extravasation)", "Bradycardia", "Arrhythmias", "Peripheral ischemia"],
    nursingConsiderations: [
      "MUST use central line (high extravasation risk)",
      "Monitor IV site hourly",
      "Titrate to MAP goal (usually >65)",
      "Have phentolamine available for extravasation",
      "Never stop abruptly - wean gradually"
    ],
    nclexTip: "Levophed = 'leave 'em dead' if you don't monitor carefully. Always use central line!"
  },
  "propofol": {
    genericName: "Propofol (Diprivan)",
    drugClass: "Sedative-Hypnotic / General Anesthetic",
    mechanism: "Enhances GABA activity, producing sedation and amnesia",
    indications: ["ICU sedation", "Procedural sedation", "General anesthesia"],
    sideEffects: ["Hypotension", "Respiratory depression", "Propofol infusion syndrome", "Pain on injection"],
    nursingConsiderations: [
      "Monitor for hypotension and respiratory depression",
      "Change tubing every 12 hours (lipid-based)",
      "Monitor triglycerides with prolonged use",
      "Watch for propofol infusion syndrome (metabolic acidosis, rhabdomyolysis)",
      "Assess sedation level regularly (RASS)"
    ],
    nclexTip: "Propofol is a lipid emulsion - count calories from it in TPN calculations"
  },
  "acetaminophen": {
    genericName: "Acetaminophen (Tylenol)",
    drugClass: "Non-opioid Analgesic / Antipyretic",
    mechanism: "Inhibits prostaglandin synthesis in CNS; exact mechanism unclear",
    indications: ["Mild-moderate pain", "Fever", "Osteoarthritis"],
    sideEffects: ["Hepatotoxicity (overdose)", "Rare allergic reactions"],
    nursingConsiderations: [
      "Max dose: 3-4g/day (lower in liver disease)",
      "Check ALL medications for hidden acetaminophen",
      "Monitor LFTs with chronic use",
      "Antidote for overdose: N-acetylcysteine (Mucomyst)",
      "Avoid in severe liver disease"
    ],
    nclexTip: "Acetaminophen is in MANY combo products - always check total daily dose to prevent liver damage"
  },
  "ondansetron": {
    genericName: "Ondansetron (Zofran)",
    drugClass: "Serotonin (5-HT3) Receptor Antagonist",
    mechanism: "Blocks serotonin receptors in CTZ and vagal nerve terminals",
    indications: ["Nausea/vomiting", "Chemotherapy-induced N/V", "Post-operative N/V"],
    sideEffects: ["Headache", "Constipation", "QT prolongation", "Dizziness"],
    nursingConsiderations: [
      "Monitor for QT prolongation (ECG)",
      "May cause constipation",
      "Give before chemotherapy or surgery",
      "Can give IV, IM, or PO"
    ],
    nclexTip: "Ondansetron is the 'go-to' antiemetic for chemo and post-op nausea"
  },
  "cefazolin": {
    genericName: "Cefazolin (Ancef)",
    drugClass: "First-Generation Cephalosporin",
    mechanism: "Inhibits bacterial cell wall synthesis",
    indications: ["Surgical prophylaxis", "Skin infections", "UTI", "Bone infections"],
    sideEffects: ["Allergic reactions", "GI upset", "Superinfection", "Cross-reactivity with penicillin"],
    nursingConsiderations: [
      "Ask about penicillin allergy (10% cross-reactivity)",
      "Give within 60 min before surgical incision",
      "Monitor for signs of allergic reaction",
      "Complete full course of therapy"
    ],
    nclexTip: "Cephalosporins have generations 1-5. First-gen (cefazolin) = gram-positive coverage, good for surgical prophylaxis"
  },
  "ketorolac": {
    genericName: "Ketorolac (Toradol)",
    drugClass: "NSAID",
    mechanism: "Inhibits cyclooxygenase (COX), reducing prostaglandin synthesis",
    indications: ["Moderate-severe pain (short-term)", "Post-operative pain"],
    sideEffects: ["GI bleeding", "Renal impairment", "Bleeding risk", "CV events"],
    nursingConsiderations: [
      "Max duration: 5 days (high GI bleed risk)",
      "Avoid in renal impairment, bleeding disorders",
      "Not for pre-operative use (bleeding risk)",
      "Monitor renal function",
      "Contraindicated post-CABG"
    ],
    nclexTip: "Ketorolac is a powerful NSAID - limit to 5 days max due to GI bleeding risk"
  }
};

// Function to find medication info by searching name
const findMedicationInfo = (medName) => {
  const searchName = medName.toLowerCase();
  
  // Direct match
  for (const [key, value] of Object.entries(MEDICATION_DATABASE)) {
    if (searchName.includes(key) || key.includes(searchName.split(' ')[0].toLowerCase())) {
      return value;
    }
  }
  
  // Check generic names
  for (const [key, value] of Object.entries(MEDICATION_DATABASE)) {
    if (value.genericName.toLowerCase().includes(searchName.split(' ')[0].toLowerCase())) {
      return value;
    }
  }
  
  return null;
};

// Scenario types with icons
const SCENARIO_TYPES = [
  { id: 'medsurg', name: 'Med-Surg', icon: Building2, description: 'General medical-surgical unit', color: 'teal' },
  { id: 'icu', name: 'ICU', icon: HeartPulse, description: 'Intensive Care Unit - critical patients', color: 'red' },
  { id: 'er', name: 'Emergency', icon: Ambulance, description: 'Emergency Department - acute care', color: 'orange' },
  { id: 'pediatric', name: 'Pediatric', icon: Baby, description: 'Pediatric unit - children', color: 'blue' }
];

const AdvancedSimulation = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [scenario, setScenario] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [currentPhase, setCurrentPhase] = useState('recognize');
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [showFeedback, setShowFeedback] = useState(null);
  const [finalReport, setFinalReport] = useState(null);
  const [selectedScenarioType, setSelectedScenarioType] = useState('medsurg');
  
  // Chart review mode
  const [chartReviewMode, setChartReviewMode] = useState(false);
  const [identifiedConcerns, setIdentifiedConcerns] = useState([]);
  
  // Head-to-toe assessment state
  const [assessmentMode, setAssessmentMode] = useState(false);
  const [selectedAssessmentAreas, setSelectedAssessmentAreas] = useState([]);
  const [assessmentResults, setAssessmentResults] = useState(null);
  
  // Delegation mode
  const [delegationMode, setDelegationMode] = useState(false);
  const [delegationTask, setDelegationTask] = useState(null);
  
  // Flag Concerns mode
  const [flagConcernMode, setFlagConcernMode] = useState(false);
  const [selectedConcerns, setSelectedConcerns] = useState([]);
  
  // Flagged orders in chart review (toggle state)
  const [flaggedOrders, setFlaggedOrders] = useState({});
  const [appropriateOrders, setAppropriateOrders] = useState({}); // { flagKey: { type, name } }
  const [orderScorecard, setOrderScorecard] = useState(null);

  // Load the student's cumulative order-review scorecard whenever a chart review opens
  useEffect(() => {
    if (!chartReviewMode) return;
    axios.get('/advanced/order-review-scorecard', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(res => setOrderScorecard(res.data))
      .catch(() => {});
  }, [chartReviewMode]);
  
  // Medication lookup modal
  const [medicationLookup, setMedicationLookup] = useState(null);
  
  // "Why I Flagged This" modal
  const [flagReasonModal, setFlagReasonModal] = useState(null); // { flagKey, medName, medInfo }
  const [flaggedReasons, setFlaggedReasons] = useState({}); // { flagKey: { reasons: [], notes: '' } }
  
  // Patient priority sequence (1st, 2nd, 3rd)
  const [patientPriority, setPatientPriority] = useState({}); // { patientId: priorityNumber (1, 2, or 3) }
  
  // Act phase tracking
  const [actPhaseCompleted, setActPhaseCompleted] = useState({
    chartReviewed: false,
    concernsFlagged: false,
    delegationDone: false
  });
  
  // Track interventions per patient
  const [patientResponses, setPatientResponses] = useState({}); // { patientId: latest response from backend }
  const [rapidResponseAlert, setRapidResponseAlert] = useState(null);
  const [patientInterventions, setPatientInterventions] = useState({}); // { patientId: { assessed: bool, prioritized: bool, chartReviewed: bool, delegated: bool } }
  
  // Expanded rationale modal
  const [expandedRationale, setExpandedRationale] = useState(null); // { title, content, type }
  
  // Improvement detail modal for final report
  const [selectedImprovement, setSelectedImprovement] = useState(null); // { id, title, action_taken, correct_action, clinical_significance, full_explanation, nclex_tips, category }

  // Flag reason categories for clinical reasoning
  const FLAG_REASONS = [
    { id: 'bleeding_risk', label: 'Bleeding Risk', description: 'Patient at risk for bleeding or actively bleeding', icon: Droplet },
    { id: 'hypotension', label: 'Hypotension Concern', description: 'Patient is hypotensive or medication may lower BP', icon: Gauge },
    { id: 'contraindication', label: 'Contraindication', description: 'Medication contraindicated due to patient condition', icon: XCircle },
    { id: 'dose_concern', label: 'Dose Too High/Low', description: 'Dose inappropriate for this patient', icon: AlertTriangle },
    { id: 'drug_interaction', label: 'Drug Interaction', description: 'Potential interaction with other medications', icon: Pill },
    { id: 'allergy_check', label: 'Allergy Verification', description: 'Need to verify no allergy before giving', icon: AlertCircle },
    { id: 'lab_monitoring', label: 'Lab Monitoring Needed', description: 'Requires lab monitoring (aPTT, INR, K+, etc.)', icon: FileText },
    { id: 'vital_sign_check', label: 'Vital Sign Parameters', description: 'Hold parameters based on HR, BP, or RR', icon: Activity },
    { id: 'renal_hepatic', label: 'Renal/Hepatic Concern', description: 'Dose adjustment needed for organ function', icon: Thermometer },
    { id: 'timing_issue', label: 'Timing/Frequency Issue', description: 'Timing or frequency needs clarification', icon: Clock },
    { id: 'patient_status', label: 'Patient Status Change', description: 'Patient condition has changed since order', icon: User },
    { id: 'high_alert', label: 'High-Alert Medication', description: 'Requires independent double-check', icon: Siren },
    { id: 'clarification_needed', label: 'Order Clarification', description: 'Order is unclear or incomplete', icon: MessageSquareWarning },
    { id: 'patient_education', label: 'Patient Education Required', description: 'Patient needs teaching before administration', icon: BookOpen }
  ];

  // Clinical concerns that nurses should identify (no hints given)
  const clinicalConcerns = [
    { id: 'vital_bp_low', category: 'Vital Signs', concern: 'Hypotension (Low Blood Pressure)', icon: Gauge },
    { id: 'vital_bp_high', category: 'Vital Signs', concern: 'Hypertension (High Blood Pressure)', icon: Gauge },
    { id: 'vital_hr_tachy', category: 'Vital Signs', concern: 'Tachycardia (Elevated Heart Rate)', icon: HeartPulse },
    { id: 'vital_hr_brady', category: 'Vital Signs', concern: 'Bradycardia (Low Heart Rate)', icon: HeartPulse },
    { id: 'vital_spo2_low', category: 'Vital Signs', concern: 'Hypoxia (Low Oxygen Saturation)', icon: Wind },
    { id: 'vital_rr_abnormal', category: 'Vital Signs', concern: 'Abnormal Respiratory Rate', icon: Wind },
    { id: 'vital_temp_fever', category: 'Vital Signs', concern: 'Fever/Hyperthermia', icon: Thermometer },
    { id: 'vital_temp_hypo', category: 'Vital Signs', concern: 'Hypothermia', icon: Thermometer },
    { id: 'pain_uncontrolled', category: 'Pain', concern: 'Uncontrolled Pain', icon: AlertTriangle },
    { id: 'neuro_change', category: 'Neurological', concern: 'Change in Level of Consciousness', icon: Brain },
    { id: 'cardiac_arrhythmia', category: 'Cardiovascular', concern: 'Cardiac Arrhythmia', icon: Heart },
    { id: 'cardiac_chest_pain', category: 'Cardiovascular', concern: 'Chest Pain/Discomfort', icon: Heart },
    { id: 'resp_distress', category: 'Respiratory', concern: 'Respiratory Distress', icon: Wind },
    { id: 'bleeding', category: 'Safety', concern: 'Active Bleeding/Hemorrhage', icon: Droplet },
    { id: 'fall_risk', category: 'Safety', concern: 'Fall Risk', icon: BedDouble },
    { id: 'infection_signs', category: 'Safety', concern: 'Signs of Infection/Sepsis', icon: Siren },
    { id: 'med_error_risk', category: 'Medication', concern: 'Medication Safety Concern', icon: Pill },
    { id: 'allergic_reaction', category: 'Medication', concern: 'Allergic Reaction Risk', icon: AlertCircle },
    { id: 'fluid_imbalance', category: 'Fluid/Electrolyte', concern: 'Fluid Imbalance', icon: Droplets },
    { id: 'notify_provider', category: 'Communication', concern: 'Need to Notify Provider', icon: MessageSquareWarning }
  ];

  // Assessment areas for head-to-toe
  const assessmentAreas = [
    { id: 'neurological', name: 'Neurological', icon: Brain, items: ['Level of consciousness', 'Orientation', 'Pupils', 'Speech', 'Movement/strength'] },
    { id: 'cardiovascular', name: 'Cardiovascular', icon: Heart, items: ['Heart sounds', 'Heart rate/rhythm', 'Blood pressure', 'Peripheral pulses', 'Capillary refill', 'Edema', 'JVD'] },
    { id: 'respiratory', name: 'Respiratory', icon: Wind, items: ['Breath sounds', 'Respiratory rate', 'Respiratory effort', 'Oxygen saturation', 'Cough', 'Sputum'] },
    { id: 'gastrointestinal', name: 'Gastrointestinal', icon: Activity, items: ['Bowel sounds', 'Abdomen assessment', 'Nausea/vomiting', 'Last BM', 'Diet tolerance'] },
    { id: 'genitourinary', name: 'Genitourinary', icon: Droplets, items: ['Urine output', 'Urine characteristics', 'Foley/voiding', 'Bladder distension'] },
    { id: 'integumentary', name: 'Integumentary', icon: Hand, items: ['Skin color', 'Skin temperature', 'Skin turgor', 'Wounds/dressings', 'IV sites'] },
    { id: 'pain', name: 'Pain Assessment', icon: ThermometerSun, items: ['Pain location', 'Pain intensity (0-10)', 'Pain quality', 'Aggravating factors', 'Relieving factors'] },
    { id: 'general', name: 'General Survey', icon: Gauge, items: ['Overall appearance', 'Level of distress', 'Full set of vital signs', 'Hygiene/posture'] },
    { id: 'systemic', name: 'Systemic / Infection', icon: Thermometer, items: ['Temperature trend', 'Signs of sepsis', 'Lactate/WBC review', 'Perfusion'] },
    { id: 'surgical', name: 'Surgical Site', icon: BedDouble, items: ['Incision/dressing', 'Drains/output', 'Bleeding', 'Post-op complications'] },
    { id: 'metabolic', name: 'Metabolic / Endocrine', icon: Droplet, items: ['Blood glucose', 'Electrolytes', 'Acid-base status', 'Hydration'] },
    { id: 'renal', name: 'Renal', icon: Shield, items: ['Urine output trend', 'BUN/creatinine', 'Fluid balance', 'Nephrotoxic meds'] }
  ];

  // Generate new scenario
  const generateScenario = async (scenarioType = selectedScenarioType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/advanced/scenarios/generate', {
        scenario_type: scenarioType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScenario(response.data);
      setSelectedPatient(null);
      setFeedbackHistory([]);
      setShowFeedback(null);
      setFinalReport(null);
      setChartReviewMode(false);
      setIdentifiedConcerns([]);
      setAssessmentMode(false);
      setSelectedAssessmentAreas([]);
      setDelegationMode(false);
      setFlaggedOrders({});
      setAppropriateOrders({});
      setPatientInterventions({});
    setPatientResponses({});
      setPatientPriority({});
      setActPhaseCompleted({ chartReviewed: false, concernsFlagged: false, delegationDone: false });
      setCurrentPhase('recognize');
      
      const typeInfo = SCENARIO_TYPES.find(t => t.id === scenarioType);
      toast.success(`${typeInfo?.name || 'Clinical'} scenario generated!`);
    } catch (error) {
      toast.error('Failed to generate scenario');
    } finally {
      setLoading(false);
    }
  };

  // Sync patients with backend after an intervention and surface their physiological response
  const handlePatientResponse = (data) => {
    if (data?.patients) {
      setScenario(prev => prev ? { ...prev, patients: data.patients } : prev);
      setSelectedPatient(prev => prev ? (data.patients.find(p => p.id === prev.id) || prev) : prev);
    }
    const all = [data?.patient_response, ...(data?.neglect_updates || [])].filter(Boolean);
    if (!all.length) return;
    setPatientResponses(prev => {
      const next = { ...prev };
      all.forEach(r => { next[r.patient_id] = r; });
      return next;
    });
    all.forEach(resp => {
      if (resp.rapid_response) {
        setRapidResponseAlert(resp);
      } else if (resp.outcome === 'improved') {
        toast.success(resp.message, { duration: 4000 });
      } else if (resp.outcome === 'no_change') {
        toast.warning(resp.message, { duration: 4000 });
      } else {
        toast.error(resp.message, { duration: 5000 });
      }
    });
  };

  // Make a decision
  const makeDecision = async (patientId, action, rationale = '', additionalData = {}) => {
    if (!scenario) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/decision`, {
        scenario_id: scenario.id,
        patient_id: patientId,
        action: action,
        rationale: rationale,
        ...additionalData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const { feedback, patient_updates, current_time, score, phase, clinical_reasoning_tip } = response.data;
      
      // Update scenario state
      setScenario(prev => ({
        ...prev,
        current_time,
        score
      }));
      
      setCurrentPhase(phase);
      setFeedbackHistory(prev => [...prev, feedback]);
      setShowFeedback(feedback);
      
      // Track intervention for this patient
      setPatientInterventions(prev => ({
        ...prev,
        [patientId]: {
          ...prev[patientId],
          assessed: prev[patientId]?.assessed || action === 'assess',
          prioritized: prev[patientId]?.prioritized || action === 'see_first',
          chartReviewed: prev[patientId]?.chartReviewed || action === 'flag_concern',
          delegated: prev[patientId]?.delegated || action === 'delegate'
        }
      }));
      
      // Show patient updates if any
      if (patient_updates && patient_updates.length > 0) {
        patient_updates.forEach(update => {
          if (update.status === 'DETERIORATING' || update.status === 'CRITICAL') {
            toast.error(update.message, { duration: 5000 });
          } else if (update.status === 'STABILIZING') {
            toast.success(update.message, { duration: 3000 });
          } else {
            toast.warning(update.message, { duration: 4000 });
          }
        });
        
        // Update patient status in local state
        setScenario(prev => ({
          ...prev,
          patients: prev.patients.map(p => {
            const update = patient_updates.find(u => u.patient_id === p.id);
            if (update) {
              return { ...p, current_status: update.status.toLowerCase(), vital_signs: update.new_vitals || p.vital_signs };
            }
            return p;
          })
        }));
      }
      
      handlePatientResponse(response.data);
      
      // Reset modes after decision
      setChartReviewMode(false);
      setAssessmentMode(false);
      setDelegationMode(false);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process decision');
    } finally {
      setLoading(false);
    }
  };

  // Submit identified concern from chart review
  const submitConcern = async (patientId, concernType, concernDetails) => {
    await makeDecision(patientId, concernType, concernDetails, { identified_concern: concernDetails });
  };

  // Submit head-to-toe assessment
  const submitAssessment = async (patientId) => {
    if (selectedAssessmentAreas.length === 0) {
      toast.error('Please select at least one assessment area');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/assess`, {
        scenario_id: scenario.id,
        patient_id: patientId,
        assessment_areas: selectedAssessmentAreas,
        findings: selectedAssessmentAreas
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAssessmentResults(response.data);
      setShowFeedback(response.data);
      setFeedbackHistory(prev => [...prev, response.data]);
      
      // Update score
      setScenario(prev => ({
        ...prev,
        score: (prev.score || 0) + response.data.score,
        current_time: (prev.current_time || 0) + 5
      }));
      
      // Track intervention for this patient
      setPatientInterventions(prev => ({
        ...prev,
        [patientId]: {
          ...prev[patientId],
          assessed: true
        }
      }));
      
      handlePatientResponse(response.data);
      
      if (response.data.quality === 'Complete') {
        toast.success(`Excellent assessment! +${response.data.score} points`);
      } else if (response.data.quality === 'Unfocused') {
        toast.warning(`Unfocused assessment: ${response.data.areas_extra.length} area(s) not indicated for this patient.`);
      } else if (response.data.quality === 'Partial') {
        toast.warning(`Partial assessment. Some areas missed.`);
      } else {
        toast.error(`Incomplete assessment. Critical areas not assessed.`);
      }
      
      setAssessmentMode(false);
      setSelectedAssessmentAreas([]);
      
    } catch (error) {
      toast.error('Failed to submit assessment');
    } finally {
      setLoading(false);
    }
  };

  // Handle delegation decision
  const handleDelegation = async (patientId, taskId, delegateTo) => {
    await makeDecision(patientId, 'delegate', `Delegated to ${delegateTo}`, { 
      task_id: taskId, 
      delegate_to: delegateTo 
    });
    setDelegationMode(false);
    
    // Track Act phase progress
    setActPhaseCompleted(prev => ({
      ...prev,
      delegationDone: true
    }));
  };

  // Handle flagging clinical concerns
  const submitFlaggedConcerns = async (patientId) => {
    if (selectedConcerns.length === 0) {
      toast.error('Please select at least one concern to flag');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/flag-concerns`, {
        scenario_id: scenario.id,
        patient_id: patientId,
        flagged_concerns: selectedConcerns
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowFeedback(response.data);
      setFeedbackHistory(prev => [...prev, response.data]);
      
      // Update score
      setScenario(prev => ({
        ...prev,
        score: (prev.score || 0) + response.data.total_points,
        current_time: (prev.current_time || 0) + 3
      }));
      
      // Track intervention for this patient
      setPatientInterventions(prev => ({
        ...prev,
        [patientId]: {
          ...prev[patientId],
          concernsFlagged: true
        }
      }));
      
      handlePatientResponse(response.data);
      
      if (response.data.correct_count > 0) {
        toast.success(`Good clinical judgment! ${response.data.correct_count} correct concern(s) identified. +${response.data.total_points} points`);
      }
      if (response.data.incorrect_count > 0) {
        toast.warning(`${response.data.incorrect_count} concern(s) not applicable to this patient.`);
      }
      
      setFlagConcernMode(false);
      setSelectedConcerns([]);
      
    } catch (error) {
      toast.error('Failed to submit concerns');
    } finally {
      setLoading(false);
    }
  };

  // Toggle flag for medication/order in chart review
  const toggleFlagOrder = (flagKey, type, name, medInfo = null) => {
    if (flaggedOrders[flagKey]) {
      // Unflag - remove the flag and reasons
      setFlaggedOrders(prev => {
        const newState = { ...prev };
        delete newState[flagKey];
        return newState;
      });
      setFlaggedReasons(prev => {
        const newState = { ...prev };
        delete newState[flagKey];
        return newState;
      });
    } else {
      // Flag - open the reason modal
      setFlagReasonModal({ flagKey, type, name, medInfo });
    }
  };

  // Handle submitting flag reasons
  const submitFlagReason = (selectedReasons, notes) => {
    if (!flagReasonModal) return;
    
    const { flagKey, type, name } = flagReasonModal;
    
    // Set the flag
    setFlaggedOrders(prev => ({
      ...prev,
      [flagKey]: { type, name, reasons: selectedReasons, notes }
    }));
    setAppropriateOrders(prev => {
      const next = { ...prev };
      delete next[flagKey];
      return next;
    });
    
    // Store the reasons
    setFlaggedReasons(prev => ({
      ...prev,
      [flagKey]: { reasons: selectedReasons, notes }
    }));
    
    // Close modal
    setFlagReasonModal(null);
  };

  // Submit all flagged orders from chart review
  const submitFlaggedOrders = async (patientId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get flagged items
      const flaggedItems = Object.entries(flaggedOrders)
        .filter(([key, value]) => value)
        .map(([key, value]) => value);
      
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/review-orders`, {
        scenario_id: scenario.id,
        patient_id: patientId,
        flagged_items: flaggedItems,
        appropriate_items: Object.values(appropriateOrders),
        total_items: (selectedPatient?.all_medications?.length || selectedPatient?.high_risk_meds?.length || 0) + (selectedPatient?.unsafe_orders?.length || 0)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowFeedback(response.data);
      setFeedbackHistory(prev => [...prev, response.data]);
      
      // Update score
      setScenario(prev => ({
        ...prev,
        score: (prev.score || 0) + response.data.total_points,
        current_time: (prev.current_time || 0) + 3
      }));
      
      // Track intervention for this patient
      setPatientInterventions(prev => ({
        ...prev,
        [patientId]: {
          ...prev[patientId],
          chartReviewed: true
        }
      }));
      
      handlePatientResponse(response.data);
      
      if (response.data.total_points > 0) {
        toast.success(`Chart review complete! ${response.data.correct_flags} correct flag(s). +${response.data.total_points} points`);
      } else if (response.data.total_points < 0) {
        toast.warning(`Review needs improvement. ${response.data.missed_flags} concern(s) missed.`);
      } else {
        toast.info('Chart review submitted.');
      }
      
      // Track Act phase progress
      setActPhaseCompleted(prev => ({
        ...prev,
        chartReviewed: true,
        concernsFlagged: true
      }));
      
      setChartReviewMode(false);
      setFlaggedOrders({});
      setAppropriateOrders({});
      
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  // Handle patient priority selection (1st, 2nd, 3rd)
  const setPatientPriorityOrder = (patientId, priority) => {
    setPatientPriority(prev => {
      const newPriority = { ...prev };
      
      // If another patient already has this priority, swap them
      const existingPatient = Object.keys(newPriority).find(id => newPriority[id] === priority);
      if (existingPatient && existingPatient !== patientId) {
        // Swap: give the existing patient the current patient's old priority (or remove)
        if (prev[patientId]) {
          newPriority[existingPatient] = prev[patientId];
        } else {
          delete newPriority[existingPatient];
        }
      }
      
      // Set the new priority
      if (priority === null) {
        delete newPriority[patientId];
      } else {
        newPriority[patientId] = priority;
      }
      
      return newPriority;
    });
  };

  // Submit patient priority order
  const submitPriorityOrder = async () => {
    const priorityCount = Object.keys(patientPriority).length;
    if (priorityCount < (scenario?.patients?.length || 3)) {
      toast.warning('Please assign priority to all patients (1st, 2nd, 3rd)');
      return;
    }
    
    // Convert to ordered array
    const orderedPatients = Object.entries(patientPriority)
      .sort((a, b) => a[1] - b[1])
      .map(([id]) => id);
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/prioritize`, {
        priority_order: orderedPatients
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowFeedback(response.data);
      if (response.data.resubmit) {
        toast.info('Practice resubmit: feedback shown, but only your first priority order earns points.');
        return;
      }
      setFeedbackHistory(prev => [...prev, response.data]);
      
      // Update score
      setScenario(prev => ({
        ...prev,
        score: (prev.score || 0) + response.data.points,
        current_time: (prev.current_time || 0) + 2
      }));
      
      // Track intervention for all prioritized patients
      const newInterventions = {};
      orderedPatients.forEach(patientId => {
        newInterventions[patientId] = {
          ...patientInterventions[patientId],
          prioritized: true
        };
      });
      setPatientInterventions(prev => ({ ...prev, ...newInterventions }));
      handlePatientResponse(response.data);
      
      if (response.data.correct) {
        toast.success(`Excellent prioritization! +${response.data.points} points`);
        setCurrentPhase('act');
      } else {
        toast.warning('Review the rationale for correct prioritization.');
      }
      
    } catch (error) {
      toast.error('Failed to submit prioritization');
    } finally {
      setLoading(false);
    }
  };

  // Complete Act phase and advance to Evaluate
  const completeActPhase = async () => {
    if (!scenario) return;
    
    // Check if minimum actions are completed
    const actionsCompleted = actPhaseCompleted.chartReviewed || actPhaseCompleted.delegationDone;
    
    if (!actionsCompleted) {
      toast.warning('Please complete at least one intervention: Review Chart, Flag Concerns, or Delegate tasks.');
      return;
    }
    
    setLoading(true);
    try {
      // Award points for completing Act phase
      const basePoints = 50;
      const bonusPoints = (actPhaseCompleted.chartReviewed ? 25 : 0) + 
                         (actPhaseCompleted.concernsFlagged ? 25 : 0) + 
                         (actPhaseCompleted.delegationDone ? 25 : 0);
      const totalPoints = basePoints + bonusPoints;
      
      setScenario(prev => ({
        ...prev,
        score: (prev.score || 0) + totalPoints,
        current_time: (prev.current_time || 0) + 5
      }));
      
      setCurrentPhase('evaluate');
      toast.success(`Act phase complete! +${totalPoints} points. Now evaluate patient outcomes.`);
      
    } catch (error) {
      toast.error('Failed to complete Act phase');
    } finally {
      setLoading(false);
    }
  };

  // Retry scenario
  const retryScenario = async () => {
    if (!scenario) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setScenario(response.data.scenario);
      setSelectedPatient(null);
      setFeedbackHistory([]);
      setShowFeedback(null);
      setFinalReport(null);
      setCurrentPhase('recognize');
      setChartReviewMode(false);
      setIdentifiedConcerns([]);
      setAssessmentMode(false);
      setDelegationMode(false);
      setActPhaseCompleted({ chartReviewed: false, concernsFlagged: false, delegationDone: false });
      setPatientInterventions({});
    setPatientResponses({});
      setPatientPriority({});
      
      toast.success(`Scenario reset! Retry #${response.data.retry_count}`);
    } catch (error) {
      toast.error('Failed to retry scenario');
    } finally {
      setLoading(false);
    }
  };

  // Complete scenario
  const completeScenario = async () => {
    if (!scenario) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/advanced/scenarios/${scenario.id}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFinalReport(response.data);
      toast.success('Scenario completed! Review your results.');
    } catch (error) {
      toast.error('Failed to complete scenario');
    } finally {
      setLoading(false);
    }
  };

  // Reset to scenario selection screen
  const resetToSelection = () => {
    setScenario(null);
    setSelectedPatient(null);
    setFeedbackHistory([]);
    setShowFeedback(null);
    setFinalReport(null);
    setCurrentPhase('recognize');
    setChartReviewMode(false);
    setIdentifiedConcerns([]);
    setAssessmentMode(false);
    setSelectedAssessmentAreas([]);
    setAssessmentResults(null);
    setDelegationMode(false);
    setDelegationTask(null);
    setFlagConcernMode(false);
    setSelectedConcerns([]);
    setFlaggedOrders({});
    setAppropriateOrders({});
    setMedicationLookup(null);
    setFlagReasonModal(null);
    setFlaggedReasons({});
    setPatientPriority({});
    setActPhaseCompleted({ chartReviewed: false, concernsFlagged: false, delegationDone: false });
    setPatientInterventions({});
    setPatientResponses({});
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'critical': return 'bg-red-500 text-white animate-pulse';
      case 'declining': return 'bg-orange-500 text-white';
      case 'stabilizing': return 'bg-green-500 text-white';
      case 'improving': return 'bg-emerald-600 text-white';
      case 'stable': return 'bg-blue-500 text-white';
      default: return 'bg-slate-500 text-white';
    }
  };

  // Get acuity badge (hidden from user - for internal logic only)
  const getAcuityBadge = (level) => {
    const colors = {
      1: 'bg-red-100 text-red-800 border-red-300',
      2: 'bg-orange-100 text-orange-800 border-orange-300',
      3: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      4: 'bg-green-100 text-green-800 border-green-300',
      5: 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[level] || colors[3];
  };

  // Clinical reasoning phase display with guidance
  const phaseSteps = [
    { id: 'recognize', label: 'Recognize', icon: Brain, description: 'Identify patient cues', 
      guidance: 'Review vital signs and chief complaints. Click "See First" on the patient who needs immediate attention.',
      actions: ['Review patient data', 'Identify concerning cues', 'Click "See First" on priority patient']
    },
    { id: 'analyze', label: 'Analyze', icon: Activity, description: 'Interpret findings',
      guidance: 'Click "Assess" to perform head-to-toe assessment. Correlate findings with the diagnosis.',
      actions: ['Perform assessment', 'Correlate clinical cues', 'Identify underlying problems']
    },
    { id: 'prioritize', label: 'Prioritize', icon: Target, description: 'Rank by acuity',
      guidance: 'Review all patients. Determine order of care based on ABCs and Maslow\'s hierarchy.',
      actions: ['Apply ABCs prioritization', 'Consider time-sensitive needs', 'Rank patients by acuity']
    },
    { id: 'act', label: 'Act', icon: Play, description: 'Implement interventions',
      guidance: 'Review Chart for orders, Flag concerns, and Delegate tasks appropriately.',
      actions: ['Review medication orders', 'Flag unsafe orders', 'Delegate to appropriate staff']
    },
    { id: 'evaluate', label: 'Evaluate', icon: CheckCircle, description: 'Assess outcomes',
      guidance: 'Reassess patients after interventions. Note any changes in status.',
      actions: ['Reassess patient status', 'Document outcomes', 'Adjust plan as needed']
    }
  ];

  // Get current phase guidance
  const getCurrentPhaseGuidance = () => {
    const phase = phaseSteps.find(p => p.id === currentPhase);
    return phase || phaseSteps[0];
  };

  // Delegation tasks based on ANA Nursing Scope of Practice
  // Note: "Unlicensed Assistive Personnel" (UAP) includes CNAs, PCTs, nursing assistants
  // All tasks show all 3 delegation options - nurse must determine appropriate choice
  // The Five Rights of Delegation: Right Task, Right Circumstance, Right Person, Right Direction, Right Supervision
  const getDelegationTasks = (patient) => {
    const allRoles = ['Unlicensed Assistive Personnel', 'LPN', 'RN'];
    
    // Complete task library organized by correct delegation
    const uapTasks = [
      { id: 'vitals', task: 'Obtain vital signs', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'ambulation', task: 'Assist with ambulation', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'feeding', task: 'Assist with feeding (no swallow precautions)', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'bathing', task: 'Provide bathing assistance', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'positioning', task: 'Reposition patient per turning schedule', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'intake_output', task: 'Record intake and output', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'specimen_collection', task: 'Collect routine urine specimen', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
      { id: 'transport', task: 'Transport stable patient to radiology', canDelegateTo: allRoles, correctAnswer: 'Unlicensed Assistive Personnel' },
    ];
    
    const lpnTasks = [
      { id: 'dressing', task: 'Simple clean dressing change', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'oral_medication', task: 'Administer oral medications', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'subq_injection', task: 'Administer subcutaneous insulin', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'im_injection', task: 'Administer IM injection', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'foley_care', task: 'Provide Foley catheter care', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'trach_suctioning', task: 'Suction established tracheostomy', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'ng_feeding', task: 'Administer NG tube feeding', canDelegateTo: allRoles, correctAnswer: 'LPN' },
      { id: 'blood_glucose', task: 'Check blood glucose and give sliding scale insulin', canDelegateTo: allRoles, correctAnswer: 'LPN' },
    ];
    
    const rnTasks = [
      { id: 'iv_push', task: 'Administer IV push medication', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'medication', task: 'Administer IV push Morphine', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'assessment', task: 'Perform initial nursing assessment', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'teaching', task: 'Provide comprehensive discharge teaching', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'care_planning', task: 'Develop nursing care plan', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'blood_transfusion', task: 'Initiate blood transfusion', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'central_line', task: 'Administer medication via central line', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'triage', task: 'Perform patient triage', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'admission_assessment', task: 'Complete admission assessment', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'iv_insertion', task: 'Insert peripheral IV', canDelegateTo: allRoles, correctAnswer: 'RN' },
      { id: 'pca_pump', task: 'Program PCA pump', canDelegateTo: allRoles, correctAnswer: 'RN' },
    ];
    
    // Select tasks based on patient acuity - mix of all delegation levels for educational value
    if (patient.acuity_level === 1) {
      // Critical patients - focus on RN tasks and urgent assessments
      return [
        rnTasks.find(t => t.id === 'iv_push'),
        rnTasks.find(t => t.id === 'assessment'),
        uapTasks.find(t => t.id === 'vitals'),
        rnTasks.find(t => t.id === 'blood_transfusion') || rnTasks.find(t => t.id === 'central_line'),
      ].filter(Boolean);
    } else if (patient.acuity_level === 2) {
      // Declining/urgent patients - mix of RN and LPN tasks
      return [
        rnTasks.find(t => t.id === 'medication'),
        lpnTasks.find(t => t.id === 'dressing'),
        uapTasks.find(t => t.id === 'vitals'),
        lpnTasks.find(t => t.id === 'subq_injection') || lpnTasks.find(t => t.id === 'oral_medication'),
      ].filter(Boolean);
    } else {
      // Stable patients - more variety including delegatable tasks
      return [
        uapTasks.find(t => t.id === 'vitals'),
        lpnTasks.find(t => t.id === 'oral_medication'),
        uapTasks.find(t => t.id === 'ambulation'),
        rnTasks.find(t => t.id === 'teaching'),
      ].filter(Boolean);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <nav className="border-b bg-white/90 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <Stethoscope className="w-8 h-8 text-primary" />
              <div>
                <span className="text-xl font-bold text-primary">Advanced Clinical Reasoning</span>
                <p className="text-xs text-slate-500">Prioritization, Assessment & Delegation</p>
              </div>
            </div>
          </div>
          {scenario && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium">Score: <span className="text-primary">{scenario.score || 0}</span></p>
                <p className="text-xs text-slate-500">Time: {scenario.current_time || 0} min</p>
              </div>
              <Button variant="outline" onClick={resetToSelection} disabled={loading} data-testid="header-new-scenario-btn">
                New
              </Button>
              <Button variant="outline" onClick={retryScenario} disabled={loading}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          )}
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* No scenario - Start screen */}
        {!scenario && (
          <div className="max-w-3xl mx-auto text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-primary to-teal-600 flex items-center justify-center">
              <Target className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-slate-800 mb-4">Advanced Clinical Scenarios</h1>
            <p className="text-lg text-slate-600 mb-8">
              Practice clinical decision-making with multiple patients. Assess patients, review charts, 
              identify safety concerns, and make delegation decisions.
            </p>
            
            <div className="grid md:grid-cols-4 gap-4 mb-8 text-left">
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <Stethoscope className="w-8 h-8 text-blue-500 mb-2" />
                <h3 className="font-bold text-slate-800">Head-to-Toe Assessment</h3>
                <p className="text-sm text-slate-600">Select body systems to assess and gather data</p>
              </div>
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <ClipboardList className="w-8 h-8 text-purple-500 mb-2" />
                <h3 className="font-bold text-slate-800">Chart Review</h3>
                <p className="text-sm text-slate-600">Review orders and identify safety concerns</p>
              </div>
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <Target className="w-8 h-8 text-red-500 mb-2" />
                <h3 className="font-bold text-slate-800">Prioritization</h3>
                <p className="text-sm text-slate-600">Decide which patient to see first</p>
              </div>
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <Users className="w-8 h-8 text-green-500 mb-2" />
                <h3 className="font-bold text-slate-800">Delegation</h3>
                <p className="text-sm text-slate-600">Assign tasks to appropriate team members</p>
              </div>
              <div className="p-4 bg-white rounded-xl border shadow-sm">
                <Volume2 className="w-8 h-8 text-indigo-500 mb-2" />
                <h3 className="font-bold text-slate-800">Auscultation</h3>
                <p className="text-sm text-slate-600">Listen to lung, heart, and bowel sounds</p>
              </div>
            </div>
            
            {/* Scenario Type Selection */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-slate-800 mb-3">Select Clinical Setting:</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {SCENARIO_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedScenarioType === type.id;
                  
                  // Use explicit color classes to ensure Tailwind includes them
                  const colorClasses = {
                    teal: { border: 'border-teal-500', bg: 'bg-teal-50', iconBg: 'bg-teal-500', text: 'text-teal-700', check: 'text-teal-500' },
                    red: { border: 'border-red-500', bg: 'bg-red-50', iconBg: 'bg-red-500', text: 'text-red-700', check: 'text-red-500' },
                    orange: { border: 'border-orange-500', bg: 'bg-orange-50', iconBg: 'bg-orange-500', text: 'text-orange-700', check: 'text-orange-500' },
                    blue: { border: 'border-blue-500', bg: 'bg-blue-50', iconBg: 'bg-blue-500', text: 'text-blue-700', check: 'text-blue-500' }
                  };
                  const colors = colorClasses[type.color] || colorClasses.teal;
                  
                  return (
                    <div
                      key={type.id}
                      onClick={() => setSelectedScenarioType(type.id)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all relative ${
                        isSelected 
                          ? `${colors.border} ${colors.bg} shadow-md` 
                          : 'border-slate-200 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`scenario-type-${type.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? `${colors.iconBg} text-white` : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className={`font-bold ${isSelected ? colors.text : 'text-slate-800'}`}>
                            {type.name}
                          </h4>
                          <p className="text-xs text-slate-500">{type.description}</p>
                        </div>
                      </div>
                      {isSelected && (
                        <CheckCircle className={`w-5 h-5 ${colors.check} absolute top-2 right-2`} />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <Button 
              onClick={() => generateScenario(selectedScenarioType)} data-testid="start-scenario-btn" 
              disabled={loading}
              size="lg"
              className="bg-primary hover:bg-primary/90 text-lg px-8 py-6"
            >
              {loading ? 'Generating...' : `Start ${SCENARIO_TYPES.find(t => t.id === selectedScenarioType)?.name} Scenario`}
            </Button>
          </div>
        )}

        {/* Active scenario */}
        {scenario && !finalReport && (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* Left - Clinical Reasoning Progress */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl border p-4 sticky top-24">
                <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <Brain className="w-5 h-5 text-purple-500" />
                  Clinical Reasoning Loop
                </h3>
                
                {/* Current Phase Guidance */}
                <div className="mb-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs font-bold text-purple-800 uppercase mb-1">Current Focus: {getCurrentPhaseGuidance().label}</p>
                  <p className="text-xs text-purple-700">{getCurrentPhaseGuidance().guidance}</p>
                  <div className="mt-2 space-y-1">
                    {getCurrentPhaseGuidance().actions?.map((action, i) => (
                      <div key={i} className="flex items-center gap-1 text-xs text-purple-600">
                        <ChevronRight className="w-3 h-3" />
                        {action}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-2">
                  {phaseSteps.map((step, idx) => {
                    const Icon = step.icon;
                    const isActive = step.id === currentPhase;
                    const isPast = phaseSteps.findIndex(s => s.id === currentPhase) > idx;
                    return (
                      <div 
                        key={step.id}
                        className={`p-3 rounded-lg border-2 transition-all ${
                          isActive ? 'border-primary bg-primary/5' : 
                          isPast ? 'border-green-300 bg-green-50' : 
                          'border-slate-200'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isActive ? 'bg-primary text-white' :
                            isPast ? 'bg-green-500 text-white' :
                            'bg-slate-200 text-slate-500'
                          }`}>
                            {isPast ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                          </div>
                          <div>
                            <p className={`text-sm font-medium ${isActive ? 'text-primary' : ''}`}>{step.label}</p>
                            <p className="text-xs text-slate-500">{step.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-slate-500 mb-2">Scenario Progress</p>
                  <Progress value={(feedbackHistory.length / 12) * 100} className="h-2" />
                  <p className="text-xs text-slate-400 mt-1">{feedbackHistory.length} actions taken</p>
                </div>
                
                {feedbackHistory.length >= 3 && (
                  <Button 
                    onClick={completeScenario} data-testid="complete-scenario-btn" 
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    Complete & Get Results
                  </Button>
                )}
              </div>
            </div>

            {/* Center - Patient Cards */}
            <div className="lg:col-span-5">
              <div className="mb-4">
                <h2 className="text-xl font-bold text-slate-800">{scenario.scenario?.title || 'Clinical Scenario'}</h2>
                <p className="text-sm text-slate-600">{scenario.scenario?.description}</p>
              </div>
              
              <div className="space-y-4">
                {scenario.patients?.map((patient, idx) => {
                  const isSelected = selectedPatient?.id === patient.id;
                  
                  return (
                    <div 
                      key={patient.id}
                      className={`bg-white rounded-xl border-2 p-4 cursor-pointer transition-all ${
                        isSelected ? 'border-primary shadow-lg' : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedPatient(patient)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                            <User className="w-6 h-6 text-slate-600" />
                          </div>
                          <div>
                            <h3 className="font-bold text-slate-800">{patient.name}</h3>
                            <p className="text-sm text-slate-500">Room {patient.room_number} • {patient.age} y/o</p>
                          </div>
                        </div>
                        {/* Status badge - only show after prioritization phase is complete */}
                        {/* This ensures the user must determine acuity themselves first */}
                        {Object.keys(patientPriority).length >= (scenario?.patients?.length || 3) && patient.current_status && (
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${getStatusColor(patient.current_status)}`}>
                            {patient.current_status === 'stabilizing' ? '✓ Stabilizing' : 
                             patient.current_status === 'improving' ? '✓ Improving' :
                             patient.current_status === 'stable' ? '✓ Stable' :
                             patient.current_status === 'declining' ? '⚠ Declining' :
                             patient.current_status === 'critical' ? '🚨 Critical' : 
                             patient.current_status}
                          </span>
                        )}
                      </div>
                      
                      {/* Intervention Progress Indicators */}
                      {patientInterventions[patient.id] && (
                        <div className="flex gap-1 mb-2">
                          {patientInterventions[patient.id].prioritized && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">✓ Prioritized</span>
                          )}
                          {patientInterventions[patient.id].assessed && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">✓ Assessed</span>
                          )}
                          {patientInterventions[patient.id].chartReviewed && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">✓ Chart</span>
                          )}
                          {patientInterventions[patient.id].concernsFlagged && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full">✓ Concerns</span>
                          )}
                          {patientInterventions[patient.id].delegated && (
                            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs rounded-full">✓ Delegated</span>
                          )}
                        </div>
                      )}
                      
                      <p className="text-sm font-medium text-slate-700 mb-2">{patient.diagnosis}</p>
                      <p className="text-sm text-slate-600 mb-3 italic">"{patient.chief_complaint}"</p>
                      
                      {/* Vital Signs - User must interpret these */}
                      <PatientVitalsGrid patientId={patient.id} vitals={patient.vital_signs} lastResponse={patientResponses[patient.id]} />
                      <PatientResponseNote patientId={patient.id} response={patientResponses[patient.id]} />
                      <VitalsTrendChart patientId={patient.id} history={patient.vitals_history} />
                      
                      {/* Priority Selection - Toggle 1st, 2nd, 3rd */}
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-slate-500 mb-2">Set patient priority order:</p>
                        <div className="flex gap-2 mb-3">
                          {[1, 2, 3].map(priority => {
                            const isSelected = patientPriority[patient.id] === priority;
                            const isUsedByOther = !isSelected && Object.values(patientPriority).includes(priority);
                            return (
                              <button
                                key={priority}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelected) {
                                    setPatientPriorityOrder(patient.id, null);
                                  } else {
                                    setPatientPriorityOrder(patient.id, priority);
                                  }
                                }}
                                className={`w-10 h-10 rounded-full font-bold text-lg transition-all ${
                                  isSelected 
                                    ? priority === 1 ? 'bg-red-500 text-white ring-2 ring-red-300' 
                                    : priority === 2 ? 'bg-orange-500 text-white ring-2 ring-orange-300'
                                    : 'bg-yellow-500 text-white ring-2 ring-yellow-300'
                                    : isUsedByOther
                                    ? 'bg-slate-100 text-slate-300 cursor-not-allowed'
                                    : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                                }`}
                                disabled={isUsedByOther && !isSelected}
                                title={`${isSelected ? 'Click to remove' : isUsedByOther ? 'Already assigned to another patient' : `Set as ${priority === 1 ? '1st' : priority === 2 ? '2nd' : '3rd'} priority`}`}
                              >
                                {priority}{priority === 1 ? 'st' : priority === 2 ? 'nd' : 'rd'}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedPatient(patient);
                            setAssessmentMode(true);
                            setChartReviewMode(false);
                            setDelegationMode(false);
                            setFlagConcernMode(false);
                          }}
                          disabled={loading}
                        >
                          <Stethoscope className="w-4 h-4 mr-1" />
                          Assess
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedPatient(patient);
                            setChartReviewMode(true);
                            setAssessmentMode(false);
                            setDelegationMode(false);
                            setFlagConcernMode(false);
                          }}
                          disabled={loading}
                        >
                          <ClipboardList className="w-4 h-4 mr-1" />
                          Review Chart
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedPatient(patient);
                            setDelegationMode(true);
                            setAssessmentMode(false);
                            setChartReviewMode(false);
                            setFlagConcernMode(false);
                          }}
                          disabled={loading}
                        >
                          <Users className="w-4 h-4 mr-1" />
                          Delegate
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          data-testid={`flag-concerns-btn-${patient.id}`}
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            setSelectedPatient(patient);
                            setFlagConcernMode(true);
                            setSelectedConcerns([]);
                            setAssessmentMode(false);
                            setChartReviewMode(false);
                            setDelegationMode(false);
                          }}
                          disabled={loading}
                        >
                          <Flag className="w-4 h-4 mr-1" />
                          Flag Concerns
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Submit Prioritization Button - Only shows during recognize/analyze/prioritize phases */}
              {['recognize', 'analyze', 'prioritize'].includes(currentPhase) && (
                <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-200">
                  <h4 className="text-sm font-bold text-purple-800 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Patient Prioritization
                  </h4>
                  <p className="text-xs text-purple-600 mb-3">
                    Assign priority order (1st, 2nd, 3rd) to all patients based on acuity, then submit.
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="flex-1 text-sm">
                      {Object.keys(patientPriority).length === 0 ? (
                        <span className="text-slate-500">No priorities assigned yet</span>
                      ) : (
                        <span className="text-purple-700">
                          {Object.keys(patientPriority).length} of {scenario?.patients?.length || 3} patients prioritized
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={submitPriorityOrder} data-testid="submit-priority-order-btn"
                      disabled={loading || Object.keys(patientPriority).length < (scenario?.patients?.length || 3)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Submit Priority Order
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Complete Act Phase Button - Only shows during Act phase */}
              {currentPhase === 'act' && (
                <div className="mt-4 p-4 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl border-2 border-teal-200">
                  <h4 className="text-sm font-bold text-teal-800 mb-2 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Complete Interventions
                  </h4>
                  <p className="text-xs text-teal-600 mb-3">
                    After reviewing charts, flagging concerns, and delegating tasks, click to proceed to evaluation.
                  </p>
                  <div className="mb-3 space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      {actPhaseCompleted.chartReviewed ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                      )}
                      <span className={actPhaseCompleted.chartReviewed ? 'text-green-700' : 'text-slate-500'}>
                        Chart Reviewed & Concerns Flagged
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      {actPhaseCompleted.delegationDone ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-4 h-4 rounded-full border-2 border-slate-300" />
                      )}
                      <span className={actPhaseCompleted.delegationDone ? 'text-green-700' : 'text-slate-500'}>
                        Tasks Delegated
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={completeActPhase}
                    disabled={loading || (!actPhaseCompleted.chartReviewed && !actPhaseCompleted.delegationDone)}
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    data-testid="complete-act-phase-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Complete Act Phase & Evaluate
                  </Button>
                </div>
              )}
            </div>

            {/* Right - Feedback, Assessment, Chart Review, Delegation */}
            <div className="lg:col-span-4 space-y-4">
              {flagConcernMode && selectedPatient && (
                <FlagConcernsPanel
                  patient={selectedPatient}
                  selected={selectedConcerns}
                  loading={loading}
                  onToggle={(id) => setSelectedConcerns(prev => prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id])}
                  onSubmit={() => submitFlaggedConcerns(selectedPatient.id)}
                  onCancel={() => { setFlagConcernMode(false); setSelectedConcerns([]); }}
                />
              )}
              {/* Current Feedback */}
              {showFeedback && !assessmentMode && !chartReviewMode && !delegationMode && !flagConcernMode && (
                <div className={`p-4 rounded-xl border-2 ${
                  showFeedback.correct !== false ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {showFeedback.correct !== false ? (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-600" />
                    )}
                    <span className={`font-bold ${showFeedback.correct !== false ? 'text-green-700' : 'text-red-700'}`}>
                      {showFeedback.review_results ? (showFeedback.correct ? 'Chart Review Complete' : 'Chart Review Needs Work')
                        : showFeedback.concern_results ? (showFeedback.correct ? 'Concerns Correctly Identified' : 'Concerns Need Review')
                        : showFeedback.correct !== false ? 'Correct!' : 'Incorrect'}
                    </span>
                    <span className={`ml-auto font-bold ${(showFeedback.total_points ?? showFeedback.points ?? 0) > 0 ? 'text-green-600' : (showFeedback.total_points ?? showFeedback.points ?? 0) < 0 ? 'text-red-600' : 'text-slate-600'}`}>
                      {showFeedback.total_points !== undefined ? (showFeedback.total_points > 0 ? '+' : '') + showFeedback.total_points : (showFeedback.points > 0 ? '+' : '') + showFeedback.points} pts
                    </span>
                  </div>
                  
                  {/* Priority Comparison Feedback - shows user's order vs correct order */}
                  {showFeedback.resubmit && (
                    <p className="mb-3 p-2 rounded bg-slate-100 border border-slate-300 text-xs text-slate-800" data-testid="priority-resubmit-note">
                      Practice resubmit - 0 points. Only your first priority order in this scenario is scored.
                    </p>
                  )}
                  {showFeedback.feedback_items && showFeedback.feedback_items.length > 0 && (
                    <div className="space-y-3 mb-4">
                      <h4 className="text-sm font-bold text-slate-700 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Priority Comparison: Your Order vs Correct Order
                      </h4>
                      <div className="grid gap-2">
                        {showFeedback.feedback_items.map((item, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg border-2 ${
                              item.correct 
                                ? 'bg-green-50 border-green-200' 
                                : 'bg-red-50 border-red-200'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {item.correct ? (
                                  <CheckCircle className="w-5 h-5 text-green-600" />
                                ) : (
                                  <XCircle className="w-5 h-5 text-red-600" />
                                )}
                                <span className="font-bold text-slate-800">{item.patient_name}</span>
                              </div>
                              <span className={`text-sm font-bold ${item.points >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {item.points > 0 ? '+' : ''}{item.points} pts
                              </span>
                            </div>
                            
                            {/* Priority comparison badges */}
                            <div className="flex items-center gap-4 mb-2">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 uppercase font-medium">Your Order:</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                                  item.correct ? 'bg-green-200 text-green-800' : 'bg-red-200 text-red-800'
                                }`}>
                                  #{item.user_priority}
                                </span>
                              </div>
                              {!item.correct && (
                                <>
                                  <ArrowRight className="w-4 h-4 text-slate-400" />
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-slate-500 uppercase font-medium">Should Be:</span>
                                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-blue-200 text-blue-800">
                                      #{item.correct_priority}
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            
                            {/* Rationale */}
                            <p className={`text-sm ${item.correct ? 'text-green-700' : 'text-red-700'}`}>
                              {item.rationale}
                            </p>
                          </div>
                        ))}
                      </div>
                      
                      {/* NCLEX Tip */}
                      {showFeedback.nclex_tip && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <h5 className="text-sm font-bold text-blue-800 mb-1">NCLEX Tip</h5>
                              <p className="text-sm text-blue-700">{showFeedback.nclex_tip}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Show rationale for single decisions */}
                  {showFeedback.rationale && !showFeedback.feedback_items && (
                    <div className="mb-3">
                      <h4 className="text-sm font-bold text-slate-700 mb-1">Rationale:</h4>
                      <p className={`text-sm ${showFeedback.correct !== false ? 'text-green-700' : 'text-red-700'}`}>
                        {showFeedback.rationale}
                      </p>
                    </div>
                  )}
                  
                  {showFeedback.areas_extra?.length > 0 && (
                    <div className="mb-3 p-2 rounded bg-amber-50 border border-amber-200" data-testid="assessment-extra-areas">
                      <h4 className="text-sm font-bold text-amber-900 mb-1">Not indicated (-3 pts each):</h4>
                      <p className="text-sm text-amber-900">{showFeedback.areas_extra.join(', ')}</p>
                    </div>
                  )}
                  
                  {showFeedback.review_results?.length > 0 && (
                    <>
                      <OrderScorecard card={showFeedback.scorecard} />
                      <ReviewResults results={showFeedback.review_results} />
                    </>
                  )}
                  
                  {/* Show concern evaluation results */}
                  {showFeedback.concern_results && (
                    <div className="space-y-2 mb-3">
                      <h4 className="text-sm font-bold text-slate-700">Evaluation:</h4>
                      {showFeedback.concern_results.map((result, i) => (
                        <div key={i} className={`p-2 rounded text-sm ${result.correct ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          <div className="flex items-center gap-2">
                            {result.correct ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                            <span className="font-medium">{result.concern}</span>
                            <span className="ml-auto">{result.points > 0 ? '+' : ''}{result.points} pts</span>
                          </div>
                          <p className="text-xs mt-1 ml-6">{result.rationale}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Show patient status in feedback */}
                  {showFeedback.patient_status && Object.keys(patientPriority).length >= (scenario?.patients?.length || 3) && (
                    <div className="p-2 rounded text-sm bg-slate-100 mb-3">
                      <strong>Patient Status:</strong> {showFeedback.patient_status}
                    </div>
                  )}
                  
                  {showFeedback.consequence && (
                    <div className={`p-2 rounded text-sm ${showFeedback.correct !== false ? 'bg-green-100' : 'bg-red-100'}`}>
                      <strong>Consequence:</strong> {showFeedback.consequence}
                    </div>
                  )}
                </div>
              )}

              {/* Head-to-Toe Assessment Mode */}
              {assessmentMode && selectedPatient && (
                <div className="bg-white rounded-xl border-2 border-blue-300 p-4">
                  <h3 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                    <Stethoscope className="w-5 h-5" />
                    Head-to-Toe Assessment: {selectedPatient.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Select the body systems you want to assess. Choose wisely based on the patient's presentation.
                  </p>
                  
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {assessmentAreas.map((area) => {
                      const Icon = area.icon;
                      const isSelected = selectedAssessmentAreas.includes(area.id);
                      // Get lung/heart/bowel sounds for this patient if area has them
                      const areaData = selectedPatient.assessment_areas?.[area.id];
                      const ausc = selectedPatient.auscultation || {};
                      const pick = (lib, ...keys) => keys.find(k => k && lib[k]);
                      const lungKey = pick(LUNG_SOUNDS, ausc.lungs, areaData?.lung_sounds, 'normal');
                      const heartKey = pick(HEART_SOUNDS, ausc.heart, areaData?.heart_sounds, 'normal_s1s2');
                      const bowelKey = pick(BOWEL_SOUNDS, ausc.bowel, areaData?.bowel_sounds, 'normal');
                      const hasLungSound = area.id === 'respiratory';
                      const hasHeartSound = area.id === 'cardiovascular';
                      const hasBowelSound = area.id === 'gastrointestinal';
                      
                      return (
                        <div 
                          key={area.id}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-blue-300'
                          }`}
                        >
                          <div 
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={() => {
                              if (isSelected) {
                                setSelectedAssessmentAreas(prev => prev.filter(a => a !== area.id));
                              } else {
                                setSelectedAssessmentAreas(prev => [...prev, area.id]);
                              }
                            }}
                          >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              isSelected ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-600'
                            }`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-slate-800">{area.name}</p>
                              <p className="text-xs text-slate-500">{area.items.slice(0, 3).join(', ')}...</p>
                            </div>
                            {isSelected && <CheckCircle className="w-5 h-5 text-blue-500" />}
                          </div>
                          
                          {/* Auscultation sounds when area is selected */}
                          {isSelected && (hasLungSound || hasHeartSound || hasBowelSound) && (
                            <div className="mt-3 pt-3 border-t border-blue-200">
                              <p className="text-xs text-blue-700 font-medium mb-2 flex items-center gap-1">
                                <Volume2 className="w-3 h-3" /> Auscultation Available
                              </p>
                              {hasLungSound && (
                                <AuscultationPlayer 
                                  type="lungs" 
                                  soundKey={lungKey}
                                  size="small"
                                  showInfo={true}
                                />
                              )}
                              {hasHeartSound && (
                                <AuscultationPlayer 
                                  type="heart" 
                                  soundKey={heartKey}
                                  size="small"
                                  showInfo={true}
                                />
                              )}
                              {hasBowelSound && (
                                <AuscultationPlayer 
                                  type="bowels" 
                                  soundKey={bowelKey}
                                  size="small"
                                  showInfo={true}
                                />
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    <Button 
                      onClick={() => submitAssessment(selectedPatient.id)} data-testid="submit-assessment-btn"
                      disabled={loading || selectedAssessmentAreas.length === 0}
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      Submit Assessment ({selectedAssessmentAreas.length} areas)
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setAssessmentMode(false);
                        setSelectedAssessmentAreas([]);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {/* Chart Review Mode - No warnings, nurse must identify concerns */}
              {chartReviewMode && selectedPatient && (
                <div className="bg-white rounded-xl border-2 border-purple-300 p-4">
                  <h3 className="font-bold text-purple-800 mb-4 flex items-center gap-2">
                    <ClipboardList className="w-5 h-5" />
                    Chart Review: {selectedPatient.name}
                  </h3>
                  
                  {/* All orders - neutral list; student decides Appropriate or Flag for each */}
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-slate-800 mb-1">Current Orders:</h4>
                    <p className="text-xs text-slate-600 mb-3">Decide for every order whether it is appropriate for this patient or should be flagged and questioned. Results appear after you submit.</p>
                    <OrderScorecard card={orderScorecard} />
                    {(() => {
                      const meds = (selectedPatient.all_medications || selectedPatient.high_risk_meds || []).map((med, i) => ({
                        flagKey: `med-${i}`, type: 'medication', name: med.name, route: med.route, frequency: med.frequency,
                        indication: med.indication, medInfo: findMedicationInfo(med.name), raw: med,
                      }));
                      const extra = (selectedPatient.unsafe_orders || [])
                        .filter(order => !(selectedPatient.all_medications || []).some(m => m.name === order.order))
                        .map((order, i) => ({ flagKey: `order-${i}`, type: 'order', name: order.order }));
                      const orders = [...meds, ...extra];
                      const decided = orders.filter(o => flaggedOrders[o.flagKey] || appropriateOrders[o.flagKey]).length;
                      return (
                        <>
                          <OrderReviewList
                            orders={orders}
                            flagged={flaggedOrders}
                            appropriate={appropriateOrders}
                            flaggedReasons={flaggedReasons}
                            reasonLabel={(r) => FLAG_REASONS.find(fr => fr.id === r)?.label}
                            onLookup={(o) => setMedicationLookup({ name: o.name, info: o.medInfo })}
                            onAppropriate={(o) => {
                              setFlaggedOrders(prev => { const n = { ...prev }; delete n[o.flagKey]; return n; });
                              setAppropriateOrders(prev => ({ ...prev, [o.flagKey]: { type: o.type, name: o.name } }));
                            }}
                            onFlag={(o) => {
                              if (flaggedOrders[o.flagKey]) {
                                toggleFlagOrder(o.flagKey, o.type, o.name, o.raw);
                              } else {
                                setFlagReasonModal({ flagKey: o.flagKey, type: o.type, name: o.name, medInfo: o.raw });
                              }
                            }}
                          />
                          <p className="text-xs text-slate-600 mt-2" data-testid="orders-decided-count">{decided} of {orders.length} orders reviewed</p>
                          <Button 
                            className="w-full mt-3 bg-purple-600 hover:bg-purple-700"
                            onClick={() => submitFlaggedOrders(selectedPatient.id)} data-testid="submit-chart-review-btn"
                            disabled={loading || decided < orders.length}
                          >
                            Submit Review ({decided}/{orders.length} reviewed)
                          </Button>
                        </>
                      );
                    })()}
                  </div>
                  
                  <Button 
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      setChartReviewMode(false);
                      setFlaggedOrders({});
                      setAppropriateOrders({});
                    }}
                  >
                    Close Chart
                  </Button>
                </div>
              )}

              {/* Delegation Mode */}
              {delegationMode && selectedPatient && (
                <div className="bg-white rounded-xl border-2 border-green-300 p-4">
                  <h3 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Delegation: {selectedPatient.name}
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Select the appropriate team member to delegate each task.
                  </p>
                  
                  <div className="space-y-4">
                    {getDelegationTasks(selectedPatient).map((task) => (
                      <div key={task.id} className="p-3 bg-slate-50 rounded-lg border">
                        <p className="font-medium text-slate-800 mb-2">{task.task}</p>
                        <div className="flex flex-wrap gap-2">
                          {task.canDelegateTo.map((role) => {
                            // Display abbreviation for button, full name for value
                            const displayName = role === 'Unlicensed Assistive Personnel' ? 'Unlicensed Assistive Personnel' : role;
                            const isUnlicensed = role === 'Unlicensed Assistive Personnel';
                            return (
                              <Button
                                key={role}
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelegation(selectedPatient.id, task.id, role)}
                                disabled={loading}
                                title={role}
                                className={`text-xs ${
                                  role === 'RN' ? 'hover:bg-blue-50 hover:border-blue-300' :
                                  role === 'LPN' ? 'hover:bg-purple-50 hover:border-purple-300' :
                                  'hover:bg-green-50 hover:border-green-300'
                                } ${isUnlicensed ? 'px-2' : ''}`}
                              >
                                {displayName}
                              </Button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Button 
                    variant="outline"
                    className="w-full mt-4"
                    onClick={() => setDelegationMode(false)}
                  >
                    Cancel
                  </Button>
                </div>
              )}

              {/* Selected Patient Details */}
              {selectedPatient && !assessmentMode && !chartReviewMode && !delegationMode && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    Patient Details: {selectedPatient.name}
                  </h3>
                  
                  {/* Clinical Cues - User must interpret */}
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Observations:</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedPatient.clinical_cues?.map((cue, i) => (
                        <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-700 text-xs rounded">
                          {cue}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  {/* Required Actions - User must identify */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Potential Actions:</h4>
                    <ul className="space-y-1">
                      {selectedPatient.required_actions?.slice(0, 4).map((action, i) => (
                        <li key={i} className="text-sm text-slate-600 flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Decision History */}
              {feedbackHistory.length > 0 && !assessmentMode && !chartReviewMode && !delegationMode && !flagConcernMode && (
                <div className="bg-white rounded-xl border p-4">
                  <h3 className="font-bold text-slate-800 mb-3">Decision History</h3>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {feedbackHistory.slice().reverse().map((fb, i) => (
                      <div key={i} className={`p-2 rounded text-sm ${fb.correct ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-xs" data-testid={`history-title-${i}`}>{historyTitle(fb)}</span>
                          <span className={`text-xs font-bold ${(fb.points ?? fb.total_points ?? 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {(fb.points ?? fb.total_points ?? 0) > 0 ? '+' : ''}{fb.points ?? fb.total_points ?? 0}
                          </span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1 line-clamp-2">{(fb.rationale || fb.overall_feedback || fb.clinical_tip || fb.nclex_tip || '').substring(0, 100)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Final Report */}
        {finalReport && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl border-2 shadow-lg p-8">
              <div className="text-center mb-8">
                <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${
                  finalReport.percentage >= 80 ? 'bg-green-100' :
                  finalReport.percentage >= 65 ? 'bg-yellow-100' :
                  'bg-red-100'
                }`}>
                  <Award className={`w-12 h-12 ${
                    finalReport.percentage >= 80 ? 'text-green-600' :
                    finalReport.percentage >= 65 ? 'text-yellow-600' :
                    'text-red-600'
                  }`} />
                </div>
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Scenario Complete!</h1>
                <p className="text-lg text-slate-600">{finalReport.performance_level} Performance</p>
              </div>
              
              {/* Score Display */}
              <div className="grid md:grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-4xl font-bold text-primary">{finalReport.total_score}</p>
                  <p className="text-sm text-slate-500">Total Score</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className="text-4xl font-bold text-blue-600">{finalReport.percentage}%</p>
                  <p className="text-sm text-slate-500">Accuracy</p>
                </div>
                <div className="text-center p-4 bg-slate-50 rounded-xl">
                  <p className={`text-xl font-bold ${
                    finalReport.nclex_prediction.includes('PASS') ? 'text-green-600' :
                    finalReport.nclex_prediction.includes('BORDERLINE') ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>{finalReport.nclex_prediction.split(' - ')[0]}</p>
                  <p className="text-sm text-slate-500">NCLEX Prediction</p>
                </div>
              </div>
              
              {/* Detailed Feedback */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <h3 className="font-bold text-green-800 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Strengths ({finalReport.correct_decisions} correct)
                  </h3>
                  <ul className="space-y-2">
                    {finalReport.strengths?.map((s, i) => (
                      <li key={i} className="text-sm text-green-700">• {s}</li>
                    ))}
                  </ul>
                </div>
                
                <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                  <h3 className="font-bold text-amber-800 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Areas for Improvement ({finalReport.incorrect_decisions} missed)
                  </h3>
                  <p className="text-xs text-amber-600 mb-3">Click an item for detailed NCLEX tips</p>
                  <ul className="space-y-2">
                    {finalReport.improvement_details?.map((item, i) => (
                      <li 
                        key={i} 
                        onClick={() => setSelectedImprovement(item)}
                        className="text-sm text-amber-700 p-2 rounded-lg hover:bg-amber-100 cursor-pointer transition-colors flex items-center gap-2 border border-transparent hover:border-amber-300"
                        data-testid={`improvement-item-${i}`}
                      >
                        <span className="px-2 py-0.5 bg-amber-200 text-amber-800 text-xs rounded-full font-medium">
                          {item.category}
                        </span>
                        <span className="flex-1">{item.title}</span>
                        <ChevronRight className="w-4 h-4 text-amber-500" />
                      </li>
                    )) || finalReport.areas_for_improvement?.map((a, i) => (
                      <li key={i} className="text-sm text-amber-700">• {a}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              {/* Recommendations */}
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 mb-8">
                <h3 className="font-bold text-blue-800 mb-3">Study Recommendations</h3>
                <ul className="space-y-2">
                  {finalReport.recommendations?.map((r, i) => (
                    <li key={i} className="text-sm text-blue-700 flex items-center gap-2">
                      <ChevronRight className="w-4 h-4" />
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Session Progress */}
              {finalReport.session_progress && (
                <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-200 mb-8">
                  <h3 className="font-bold text-indigo-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Session Progress - {SCENARIO_TYPES.find(t => t.id === finalReport.scenario_type)?.name || 'Med-Surg'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-indigo-600">{finalReport.session_progress.scenarios_completed}</p>
                      <p className="text-xs text-slate-500">Scenarios Completed</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-indigo-600">{finalReport.session_progress.average_percentage}%</p>
                      <p className="text-xs text-slate-500">Average Score</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-green-600">{finalReport.session_progress.best_percentage}%</p>
                      <p className="text-xs text-slate-500">Best Score</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded-lg">
                      <p className="text-2xl font-bold text-blue-600">
                        {finalReport.session_progress.total_correct}/{finalReport.session_progress.total_correct + finalReport.session_progress.total_incorrect}
                      </p>
                      <p className="text-xs text-slate-500">Total Correct</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex flex-wrap justify-center gap-4">
                <Button onClick={retryScenario} variant="outline" size="lg">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Retry Scenario
                </Button>
                <Button 
                  onClick={() => generateScenario(finalReport.scenario_type || selectedScenarioType)} 
                  size="lg" 
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="continue-scenario-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Continue ({SCENARIO_TYPES.find(t => t.id === (finalReport.scenario_type || selectedScenarioType))?.name})
                </Button>
                <Button onClick={resetToSelection} size="lg" variant="outline" data-testid="new-scenario-btn">
                  Change Scenario Type
                </Button>
                <Button onClick={() => navigate('/dashboard')} variant="outline" size="lg">
                  Back to Dashboard
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Medication Lookup Modal */}
        {medicationLookup && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-t-2xl flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Pill className="w-6 h-6" />
                    <h2 className="text-xl font-bold">Drug Reference</h2>
                  </div>
                  <p className="text-blue-100 text-sm">{medicationLookup.info.genericName}</p>
                </div>
                <button
                  onClick={() => setMedicationLookup(null)}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                {/* Drug Class */}
                <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                  <h3 className="text-xs font-bold text-indigo-600 uppercase mb-1">Drug Class</h3>
                  <p className="text-indigo-900 font-medium">{medicationLookup.info.drugClass}</p>
                </div>
                
                {/* Mechanism of Action */}
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="text-xs font-bold text-purple-600 uppercase mb-1">Mechanism of Action</h3>
                  <p className="text-purple-900 text-sm">{medicationLookup.info.mechanism}</p>
                </div>
                
                {/* Indications */}
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-xs font-bold text-green-600 uppercase mb-1">Common Indications</h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {medicationLookup.info.indications.map((ind, i) => (
                      <span key={i} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {ind}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Side Effects */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <h3 className="text-xs font-bold text-amber-600 uppercase mb-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Side Effects
                  </h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {medicationLookup.info.sideEffects.map((se, i) => (
                      <span key={i} className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full">
                        {se}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Nursing Considerations */}
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-xs font-bold text-blue-600 uppercase mb-2 flex items-center gap-1">
                    <Stethoscope className="w-3 h-3" />
                    Nursing Considerations
                  </h3>
                  <ul className="space-y-1">
                    {medicationLookup.info.nursingConsiderations.map((nc, i) => (
                      <li key={i} className="text-sm text-blue-800 flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        {nc}
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* NCLEX Tip */}
                <div className="p-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-lg border-2 border-rose-300">
                  <h3 className="text-xs font-bold text-rose-600 uppercase mb-1 flex items-center gap-1">
                    <Award className="w-3 h-3" />
                    NCLEX Tip
                  </h3>
                  <p className="text-rose-800 text-sm font-medium">{medicationLookup.info.nclexTip}</p>
                </div>
              </div>
              
              {/* Footer */}
              <div className="sticky bottom-0 bg-slate-100 p-4 rounded-b-2xl border-t">
                <Button
                  onClick={() => setMedicationLookup(null)}
                  className="w-full bg-slate-700 hover:bg-slate-800"
                >
                  Close Reference
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* "Why I Flagged This" Modal */}
        {flagReasonModal && (
          <FlagReasonModal 
            medication={flagReasonModal}
            reasons={FLAG_REASONS}
            onSubmit={submitFlagReason}
            onCancel={() => setFlagReasonModal(null)}
          />
        )}
        
        {/* Expanded Rationale Modal */}
        {expandedRationale && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-6 h-6" />
                      <h2 className="text-xl font-bold">Complete Rationale</h2>
                    </div>
                    <p className="text-blue-100 text-sm">{expandedRationale.title}</p>
                  </div>
                  <button
                    onClick={() => setExpandedRationale(null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-5 space-y-4">
                {/* Status Badge */}
                {expandedRationale.content.isAppropriate !== undefined && (
                  <div className={`p-3 rounded-lg ${
                    expandedRationale.content.isAppropriate === false
                      ? 'bg-red-50 border-2 border-red-300'
                      : expandedRationale.content.shouldFlag
                      ? 'bg-amber-50 border-2 border-amber-300'
                      : 'bg-green-50 border-2 border-green-300'
                  }`}>
                    <div className="flex items-center gap-2">
                      {expandedRationale.content.isAppropriate === false ? (
                        <>
                          <XCircle className="w-5 h-5 text-red-600" />
                          <span className="font-bold text-red-800">Order Requires Clarification</span>
                        </>
                      ) : expandedRationale.content.shouldFlag ? (
                        <>
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                          <span className="font-bold text-amber-800">Appropriate Therapy - Monitor Required</span>
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="font-bold text-green-800">Appropriate Therapy - Safe to Administer</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Route and Frequency */}
                {(expandedRationale.content.route || expandedRationale.content.frequency) && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <div className="flex gap-4">
                      {expandedRationale.content.route && (
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase">Route</span>
                          <p className="text-slate-800 font-medium">{expandedRationale.content.route}</p>
                        </div>
                      )}
                      {expandedRationale.content.frequency && (
                        <div>
                          <span className="text-xs font-bold text-slate-500 uppercase">Frequency</span>
                          <p className="text-slate-800 font-medium">{expandedRationale.content.frequency}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Full Rationale */}
                {expandedRationale.content.rationale && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h3 className="text-sm font-bold text-blue-700 uppercase mb-2 flex items-center gap-1">
                      <Info className="w-4 h-4" />
                      Clinical Rationale
                    </h3>
                    <p className="text-blue-900 leading-relaxed">{expandedRationale.content.rationale}</p>
                  </div>
                )}
                
                {/* Safety/Flag Information */}
                {expandedRationale.content.flagReason && (
                  <div className={`p-4 rounded-lg border-2 ${
                    expandedRationale.content.isAppropriate === false
                      ? 'bg-red-50 border-red-300'
                      : 'bg-amber-50 border-amber-300'
                  }`}>
                    <h3 className={`text-sm font-bold uppercase mb-2 flex items-center gap-1 ${
                      expandedRationale.content.isAppropriate === false ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      <Siren className="w-4 h-4" />
                      {expandedRationale.content.isAppropriate === false ? 'Safety Concern' : 'Monitoring Required'}
                    </h3>
                    <p className={`leading-relaxed ${
                      expandedRationale.content.isAppropriate === false ? 'text-red-800' : 'text-amber-800'
                    }`}>
                      {expandedRationale.content.flagReason}
                    </p>
                  </div>
                )}
                
                {/* Delegation rationale */}
                {expandedRationale.content.delegationRationale && (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h3 className="text-sm font-bold text-purple-700 uppercase mb-2">Delegation Rationale</h3>
                    <p className="text-purple-900 leading-relaxed">{expandedRationale.content.delegationRationale}</p>
                  </div>
                )}
              </div>
              
              {/* Footer */}
              <div className="sticky bottom-0 bg-slate-100 p-4 rounded-b-2xl border-t">
                <Button
                  onClick={() => setExpandedRationale(null)}
                  className="w-full bg-slate-700 hover:bg-slate-800"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
        
        <RapidResponseAlert alert={rapidResponseAlert} onClose={() => setRapidResponseAlert(null)} />
        
        {/* Improvement Detail Modal */}
        {selectedImprovement && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="improvement-modal">
            <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              {/* Header */}
              <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 text-white p-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <BookOpen className="w-6 h-6" />
                      <h2 className="text-xl font-bold">Improvement Area</h2>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="px-2 py-0.5 bg-white/20 text-white text-xs rounded-full font-medium">
                        {selectedImprovement.category}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedImprovement(null)}
                    className="p-2 hover:bg-white/20 rounded-full transition-colors"
                    data-testid="close-improvement-modal"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              
              <div className="p-5 space-y-4">
                {/* What Happened */}
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h3 className="text-sm font-bold text-red-700 uppercase mb-2 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    What Went Wrong
                  </h3>
                  <p className="text-red-800 leading-relaxed">{selectedImprovement.full_explanation}</p>
                </div>
                
                {/* Clinical Significance */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <h3 className="text-sm font-bold text-purple-700 uppercase mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Why This Matters
                  </h3>
                  <p className="text-purple-800 leading-relaxed">{selectedImprovement.clinical_significance}</p>
                </div>
                
                {/* NCLEX Tips */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="text-sm font-bold text-blue-700 uppercase mb-3 flex items-center gap-2">
                    <Award className="w-4 h-4" />
                    NCLEX Tips for This Topic
                  </h3>
                  <ul className="space-y-3">
                    {selectedImprovement.nclex_tips?.map((tip, i) => (
                      <li key={i} className="flex gap-3 text-blue-800">
                        <span className="flex-shrink-0 w-6 h-6 bg-blue-200 text-blue-700 rounded-full flex items-center justify-center text-sm font-bold">
                          {i + 1}
                        </span>
                        <span className="leading-relaxed">{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                {/* Quick Reference */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="text-sm font-bold text-green-700 uppercase mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Remember for Next Time
                  </h3>
                  <div className="text-green-800 space-y-2">
                    {selectedImprovement.category === 'Prioritization' && (
                      <p><strong>ABCs:</strong> Airway → Breathing → Circulation → then other needs</p>
                    )}
                    {selectedImprovement.category === 'Assessment' && (
                      <p><strong>Nursing Process:</strong> Assess → Diagnose → Plan → Implement → Evaluate</p>
                    )}
                    {selectedImprovement.category === 'Delegation' && (
                      <p><strong>5 Rights:</strong> Right Task, Right Circumstance, Right Person, Right Direction, Right Supervision</p>
                    )}
                    {selectedImprovement.category === 'Medication Safety' && (
                      <p><strong>6 Rights:</strong> Right Patient, Drug, Dose, Route, Time, Documentation</p>
                    )}
                    {selectedImprovement.category === 'Patient Safety' && (
                      <p><strong>Priority:</strong> Safety first - if it could harm the patient, stop and reassess</p>
                    )}
                    {selectedImprovement.category === 'Communication' && (
                      <p><strong>SBAR:</strong> Situation → Background → Assessment → Recommendation</p>
                    )}
                    {selectedImprovement.category === 'Clinical Reasoning' && (
                      <p><strong>Think:</strong> What is the most urgent need? What could go wrong?</p>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Footer */}
              <div className="sticky bottom-0 bg-slate-100 p-4 rounded-b-2xl border-t">
                <Button
                  onClick={() => setSelectedImprovement(null)}
                  className="w-full bg-amber-600 hover:bg-amber-700"
                  data-testid="close-improvement-btn"
                >
                  Got It - Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Separate component for the Flag Reason Modal
const FlagReasonModal = ({ medication, reasons, onSubmit, onCancel }) => {
  const [selectedReasons, setSelectedReasons] = useState([]);
  const [notes, setNotes] = useState('');
  
  const toggleReason = (reasonId) => {
    setSelectedReasons(prev => 
      prev.includes(reasonId) 
        ? prev.filter(r => r !== reasonId)
        : [...prev, reasonId]
    );
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-orange-500 text-white p-4 rounded-t-2xl">
          <div className="flex items-center gap-2 mb-1">
            <Flag className="w-6 h-6" />
            <h2 className="text-xl font-bold">Why Are You Flagging This?</h2>
          </div>
          <p className="text-red-100 text-sm">
            <span className="font-medium">{medication.name}</span>
          </p>
          <p className="text-red-200 text-xs mt-1">
            Select all reasons that apply to demonstrate your clinical reasoning
          </p>
        </div>
        
        <div className="p-4">
          {/* Reason checkboxes in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {reasons.map((reason) => {
              const Icon = reason.icon;
              const isSelected = selectedReasons.includes(reason.id);
              return (
                <div
                  key={reason.id}
                  onClick={() => toggleReason(reason.id)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-red-400 bg-red-50' 
                      : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      isSelected ? 'bg-red-500 border-red-500 text-white' : 'border-slate-300'
                    }`}>
                      {isSelected && <CheckCircle className="w-3 h-3" />}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-1">
                        <Icon className={`w-4 h-4 ${isSelected ? 'text-red-600' : 'text-slate-500'}`} />
                        <span className={`text-sm font-medium ${isSelected ? 'text-red-800' : 'text-slate-700'}`}>
                          {reason.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{reason.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Additional notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Additional Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add specific concerns or clarifications..."
              className="w-full p-2 border rounded-lg text-sm resize-none h-20 focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>
          
          {/* Selected count */}
          {selectedReasons.length > 0 && (
            <div className="mb-4 p-2 bg-red-50 rounded-lg text-sm text-red-700">
              <span className="font-bold">{selectedReasons.length}</span> reason(s) selected
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-100 p-4 rounded-b-2xl border-t flex gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(selectedReasons, notes)}
            disabled={selectedReasons.length === 0}
            className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-slate-300"
          >
            <Flag className="w-4 h-4 mr-2" />
            Flag Medication ({selectedReasons.length})
          </Button>
        </div>
      </div>
    </div>
  );
};


export default AdvancedSimulation;